
import React from "react";
import SectionHeader from "./SectionHeader";
import { Separator } from "@/components/ui/separator";
import { AlertTriangle } from "lucide-react";

interface ScriptureReference {
  reference: string;
  text: string;
  explanation: string;
}

interface ScriptureSectionProps {
  scriptureReferences: ScriptureReference[];
}

const ScriptureSection: React.FC<ScriptureSectionProps> = ({ scriptureReferences }) => {
  // Initial validation - handle empty or invalid data
  if (!scriptureReferences || !Array.isArray(scriptureReferences)) {
    return (
      <>
        <div>
          <SectionHeader number={3} title="Scripture References" />
          <div className="prose prose-lg max-w-none text-left">
            <p className="text-[#403E43] italic">No scripture references were provided for this answer.</p>
          </div>
        </div>
        <Separator className="bg-heaven-gold/10" />
      </>
    );
  }
  
  // Adaptive processing based on what's actually in the data
  const processedReferences = scriptureReferences
    .filter(ref => ref && typeof ref === 'object') // Filter out null/undefined items
    .map(ref => {
      // Extract reference, defaulting to placeholder if missing
      const reference = typeof ref.reference === 'string' && ref.reference.trim() 
        ? ref.reference.trim()
            .replace(/Reference:\s*/gi, '')
            .replace(/Ref:\s*/gi, '')
            .replace(/^\d+\.\s*/g, '')
        : "Scripture reference";
      
      // Extract text, defaulting to placeholder if missing  
      const text = typeof ref.text === 'string' && ref.text.trim()
        ? ref.text.trim()
            .replace(/Text:\s*/gi, '')
            .replace(/^\s*["']|["']\s*$/g, '') // Remove quotes around the text
        : "Scripture text unavailable";
      
      // Extract explanation, defaulting to empty string if missing
      const explanation = typeof ref.explanation === 'string' && ref.explanation.trim()
        ? ref.explanation.trim()
            .replace(/Explanation:\s*/gi, '')
        : "";
      
      return { reference, text, explanation };
    })
    .filter(ref => 
      // Filter out obvious placeholders or failed extractions
      !ref.reference.includes("not fully extracted") &&
      !ref.reference.toLowerCase().includes("unavailable") &&
      ref.reference !== "Scripture reference"
    );
  
  // If after processing we have no valid references, show meaningful message
  if (processedReferences.length === 0) {
    return (
      <>
        <div>
          <SectionHeader number={3} title="Scripture References" />
          <div className="prose prose-lg max-w-none text-left">
            <p className="text-[#403E43] italic">
              The scripture references couldn't be properly extracted from the response.
            </p>
          </div>
        </div>
        <Separator className="bg-heaven-gold/10" />
      </>
    );
  }

  // Remove duplicate references by normalizing and comparing
  const uniqueReferences: ScriptureReference[] = [];
  const seenReferences = new Set<string>();
  
  processedReferences.forEach(ref => {
    // Create a normalized version for comparison (remove spaces, punctuation, lowercase)
    const normalizedRef = ref.reference.toLowerCase()
      .replace(/\s+/g, '')
      .replace(/[.,;:]/g, '');
    
    if (!seenReferences.has(normalizedRef) && normalizedRef.length > 0) {
      seenReferences.add(normalizedRef);
      uniqueReferences.push(ref);
    }
  });

  return (
    <>
      <div>
        <SectionHeader number={3} title="Scripture References" />
        <div className="prose prose-lg max-w-none text-left space-y-4">
          {uniqueReferences.map((scripture, index) => (
            <div key={index} className="bg-heaven-gold/5 p-4 rounded-md mb-4">
              <p className="font-semibold text-heaven-gold">{scripture.reference}</p>
              <blockquote className="italic border-l-2 pl-4 border-heaven-gold/30 my-2">
                {scripture.text}
              </blockquote>
              {scripture.explanation && (
                <p className="text-[#403E43]">{scripture.explanation}</p>
              )}
            </div>
          ))}
        </div>
      </div>
      <Separator className="bg-heaven-gold/10" />
    </>
  );
};

export default ScriptureSection;
